import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Menu, X, Mail } from "lucide-react";
import Logo from "./Logo";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/work", label: "Work" },
  { to: "/contact", label: "Contact" },
];

const EMAIL_HREF =
  "mailto:hello@pixelkare.com?subject=AI%20App%20Development%20Enquiry&body=Hi%20Pixelkare%2C%20I%27d%20like%20to%20discuss%20building%20an%20AI-powered%20app.";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        scrolled
          ? "bg-[#faf9f6]/80 backdrop-blur-xl border-b border-black/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Logo />

        <nav className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `font-mono text-xs uppercase tracking-[0.2em] transition-colors ${
                  isActive
                    ? "text-[#0a0a0a]"
                    : "text-[#52525b] hover:text-[#0a0a0a]"
                }`
              }
              end={l.to === "/"}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={EMAIL_HREF}
            data-testid="nav-email-cta"
            className="inline-flex items-center gap-2 bg-[#1f87bc] hover:bg-[#176d99] text-white text-sm font-medium px-5 py-2.5 transition-colors"
          >
            <Mail className="h-4 w-4" />
            Email us
          </a>
        </div>

        <button
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((s) => !s)}
          className="md:hidden text-[#0a0a0a] p-2"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="md:hidden bg-[#faf9f6]/95 backdrop-blur-xl border-t border-black/10"
        >
          <div className="px-6 py-8 flex flex-col gap-6">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `font-display text-3xl ${
                    isActive ? "text-[#0a0a0a]" : "text-[#52525b]"
                  }`
                }
                end={l.to === "/"}
              >
                {l.label}.
              </NavLink>
            ))}
            <a
              href={EMAIL_HREF}
              data-testid="nav-mobile-email-cta"
              className="mt-4 inline-flex items-center justify-center gap-2 bg-[#1f87bc] text-white text-sm font-medium px-5 py-3"
            >
              <Mail className="h-4 w-4" />
              Email us
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
